/**
 * Shown wherever we turn a file away for its type: the client-side upload
 * guard, the server-side upload rejection, and the pipeline's terminal
 * UNSUPPORTED_FILE_TYPE failure. One string so a user who trips two of them
 * doesn't get two different explanations of the same rule.
 */
export const UNSUPPORTED_FILE_TYPE_MESSAGE =
  "The file type is not supported. Please upload a PDF or image file (JPG, PNG).";

/**
 * Archive content types the document pipeline can never process. Shared by
 * the API's upload validation and the client-side upload guard so the lists
 * can't drift. Kept as a blocklist (not an allowlist) because staff and
 * ingestion flows legitimately create documents with content types we can't
 * enumerate — the analysis pipeline has its own guard for everything else.
 */
const ARCHIVE_CONTENT_TYPES = new Set([
  "application/zip",
  "application/x-zip-compressed",
  "application/x-7z-compressed",
  "application/x-rar-compressed",
  "application/vnd.rar",
  "application/gzip",
  "application/x-gzip",
  "application/x-tar",
  "application/x-bzip2",
]);

export function isArchiveContentType(contentType: string): boolean {
  // The value is an arbitrary client-supplied string, so strip MIME
  // parameters ("application/zip; charset=binary") before matching.
  const [mediaType = ""] = contentType.split(";", 1);
  return ARCHIVE_CONTENT_TYPES.has(mediaType.trim().toLowerCase());
}

/**
 * Extension fallback for when the content type tells us nothing. Browsers
 * report "" or application/octet-stream whenever the OS can't identify a
 * file, which is common for the compressed formats above, and we can't
 * blocklist octet-stream itself without rejecting legitimate uploads.
 */
const ARCHIVE_EXTENSIONS = [
  ".zip",
  ".7z",
  ".rar",
  ".gz",
  ".tgz",
  ".tar",
  ".bz2",
  ".tbz2",
];

/**
 * Whether an upload is an archive by either signal. Prefer this over
 * `isArchiveContentType` anywhere a file name is available.
 */
export function isArchiveFile({
  name,
  contentType,
}: {
  name?: string | null;
  contentType?: string | null;
}): boolean {
  if (contentType && isArchiveContentType(contentType)) {
    return true;
  }
  const normalizedName = name?.trim().toLowerCase() ?? "";
  return ARCHIVE_EXTENSIONS.some(extension =>
    normalizedName.endsWith(extension),
  );
}
